import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { message, Modal } from "antd";
import {
  HolderOutlined,
  UserOutlined,
  VideoCameraOutlined,
  GlobalOutlined,
  StarOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import PsychologyAltOutlined from "@mui/icons-material/PsychologyAltOutlined";
import { RiCoinsLine } from "react-icons/ri";
import PageBreadcrumb from "../../../../reusableField/PageBreadcrumb";
import BackIcon from "../../../../Images/backIcon.png";
import HomeIcon from "../../../../Images/homeIcon.png";
import {
  getAssessmentFlow,
  saveAssessmentFlow,
} from "../../../../services/SyncApis";

/* -------------------------------------------------------------------------- */
/* Assessment definitions                                                     */
/* -------------------------------------------------------------------------- */

const ASSESSMENT_DEFINITIONS = [
  {
    id: "demographic",
    name: "Demographic Assessment",
    subtitle: "Know the candidate profile",
    tags: ["A Quiz"],
    questions: 5,
    points: "5 Points per question",
  },
  {
    id: "jpFitment",
    name: "JP Fitment Test",
    subtitle: "Evaluate job-role fit.",
    tags: [],
    questions: 5,
    points: null,
  },
  {
    id: "scenario",
    name: "Scenario Assessment",
    subtitle: "Test practical problem-solving",
    tags: ["A Video", "A Quiz"],
    questions: 5,
    points: "5 Points per question",
  },
];

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const AssessmentJourney = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const accountName = location.state?.accountName || "Account";
  const roleName =
    location.state?.name || location.state?.roleName || "Role";
  const safeRoleName = roleName;
  const fullState = location.state || {};

  useEffect(() => {
    console.log("assesment account Name", accountName);
  }, [])

  const adminRoleId = location.state?.roleId || null;
  const adminClientId = location.state?.accountId || null;

  const [enabled, setEnabled] = useState({
    demographic: true,
    jpFitment: true,
    scenario: true,
  });

  const [order, setOrder] = useState([
    "demographic",
    "jpFitment",
    "scenario",
  ]);

  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [confirmModal, setConfirmModal] = useState({
    visible: false,
    id: null,
    name: null,
    willEnable: false,
  });

  /* ------------------------------------------------------------------------ */
  /* Helpers                                                                  */
  /* ------------------------------------------------------------------------ */

  const activeOrder = order.filter((id) => enabled[id]);

  const getAssessment = (id) =>
    ASSESSMENT_DEFINITIONS.find((a) => a.id === id);

  const getDefaultOrder = () =>
    ASSESSMENT_DEFINITIONS.map((item) => item.id);

  const normalizeOrderFromAssessments = (assessments) => {
    const ordered = [...assessments]
      .sort((a, b) => a.order - b.order)
      .map((item) => item.type)
      .filter(
        (type, index, self) => self.indexOf(type) === index
      );

    const missing = getDefaultOrder().filter(
      (type) => !ordered.includes(type)
    );

    return [...ordered, ...missing];
  };

  const normalizeEnabledFromAssessments = (assessments) =>
    assessments.reduce((acc, item) => {
      acc[item.type] = item.enabled === true;
      return acc;
    }, {});

  const buildAssessmentPayload = (
    payloadOrder,
    payloadEnabled
  ) =>
    payloadOrder.map((type, index) => ({
      type,
      enabled: payloadEnabled[type] === true,
      order: index + 1,
    }));

  /* ------------------------------------------------------------------------ */
  /* Persist Assessment Flow                                                  */
  /* ------------------------------------------------------------------------ */

  const persistAssessmentFlow = async (
    nextEnabled,
    nextOrder
  ) => {
    if (!adminRoleId || !adminClientId) {
      return false;
    }

    setSaving(true);

    try {
      const payload = {
        changedBy: null,
        assessments: buildAssessmentPayload(
          nextOrder,
          nextEnabled
        ),
      };

      const response = await saveAssessmentFlow(
        adminRoleId,
        adminClientId,
        payload
      );

      if (response.success === false) {
        setValidationError(
          response.message || "Unable to save assessment configuration."
        );

        return false;
      }

      setValidationError(null);

      return true;
    } catch (error) {
      console.error(
        "Failed to save assessment flow",
        error
      );

      /*
       * Backend validation errors should be displayed
       * directly to the user.
       */
      const backendMessage =
        error.response?.data?.message;

      if (backendMessage) {
        setValidationError(backendMessage);
        return false;
      }

      message.error(
        "Unable to save assessment configuration. Please try again."
      );

      return false;
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /* Toggle                                                                    */
  /* ------------------------------------------------------------------------ */

  const handleToggleClick = (id, name) => {
    setConfirmModal({
      visible: true,
      id,
      name,
      willEnable: !enabled[id],
    });
  };

  /* ------------------------------------------------------------------------ */
  /* Load Assessment Flow                                                     */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    console.log("Assessment Journey IDs:", {
      adminRoleId,
      adminClientId,
    });

    const loadAssessmentFlow = async () => {
      if (!adminRoleId || !adminClientId) {
        console.log("GET skipped: missing IDs");
        return;
      }

      setLoading(true);

      try {
        const response = await getAssessmentFlow(
          adminRoleId,
          adminClientId
        );

        const data = response?.data ?? response;

        if (data?.assessments) {
          setEnabled(
            normalizeEnabledFromAssessments(
              data.assessments
            )
          );

          setOrder(
            normalizeOrderFromAssessments(
              data.assessments
            )
          );
        }
      } catch (error) {
        console.error(
          "Failed to load assessment flow",
          error
        );

        const backendMessage =
          error.response?.data?.message;

        if (backendMessage) {
          setValidationError(backendMessage);
        } else {
          message.error(
            "Unable to load assessment configuration."
          );
        }
      } finally {
        setLoading(false);
      }
    };

    loadAssessmentFlow();
  }, [adminRoleId, adminClientId]);

  /* ------------------------------------------------------------------------ */
  /* Confirm Toggle                                                            */
  /* ------------------------------------------------------------------------ */

  const handleConfirm = async () => {
    const {
      id,
      name,
      willEnable,
    } = confirmModal;

    setConfirmModal({
      visible: false,
      id: null,
      name: null,
      willEnable: false,
    });

    setValidationError(null);

    const nextEnabled = {
      ...enabled,
      [id]: willEnable,
    };

    let nextOrder = order;

    if (!willEnable) {
      nextOrder = order.filter(
        (item) => item !== id
      );

      setOrder(nextOrder);
      setEnabled(nextEnabled);
      setValidationError(null);

      message.error(
        `${name} Deactivated Successfully`
      );
    } else {
      const tentative = order.includes(id)
        ? order
        : [...order, id];

      nextOrder = tentative;

      setOrder(nextOrder);
      setEnabled(nextEnabled);

      message.success(
        `${name} Reactivated Successfully`
      );
    }

    await persistAssessmentFlow(
      nextEnabled,
      nextOrder
    );
  };

  const handleCancelModal = () => {
    setConfirmModal({
      visible: false,
      id: null,
      name: null,
      willEnable: false,
    });
  };

  /* ------------------------------------------------------------------------ */
  /* Drag and Drop                                                             */
  /* ------------------------------------------------------------------------ */

  const handleDragStart = (e, id) => {
    setDraggedId(id);
    setValidationError(null);

    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  };

  const handleDragOver = (e, id) => {
    e.preventDefault();

    e.dataTransfer.dropEffect = "move";

    if (id !== draggedId) {
      setDragOverId(id);
    }
  };

  const handleDrop = async (e, targetId) => {
    e.preventDefault();

    const sourceId = draggedId;

    if (!sourceId || sourceId === targetId) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }

    /*
     * IMPORTANT:
     *
     * Use the complete order here, not activeOrder.
     *
     * The frontend does NOT validate the business rule.
     * The backend is the source of truth.
     */
    const newOrder = [...order];

    const fromIdx = newOrder.indexOf(sourceId);
    const toIdx = newOrder.indexOf(targetId);

    if (fromIdx === -1 || toIdx === -1) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }

    newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, sourceId);

    const previousOrder = [...order];

    /*
     * Optimistically update the UI.
     */
    setOrder(newOrder);

    /*
     * Send every drag/drop change to backend.
     *
     * Backend decides whether the order is valid.
     */
    const saved = await persistAssessmentFlow(
      enabled,
      newOrder
    );

    /*
     * Backend rejected the order.
     * Restore the previous UI order.
     */
    if (!saved) {
      setOrder(previousOrder);
    }

    setDraggedId(null);
    setDragOverId(null);
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  /* ------------------------------------------------------------------------ */
  /* Render                                                                    */
  /* ------------------------------------------------------------------------ */

  return (
    <div style={{ minHeight: "100vh" }}>
      {/* <PageBreadcrumb /> */}

      <div style={{ padding: "10px 40px 0" }}>
        <div>
          <div className="mb-4 -mt-8 ml-1">

            {/* Back & Home Buttons */}
            <div className=" relative top-[12px] flex justify-between items-center w-full">

              {/* Back Button */}
              <button
                onClick={() =>
                  navigate(
                    "/sync-configuration",
                    {
                      state: fullState,
                    }
                  )
                }
                className="flex items-center gap-2 bg-[#C21A17] text-white px-5 py-2 rounded-lg text-sm font-semibold shadow-md hover:bg-[#A91513] transition-all duration-200"
              >
                <img
                  src={BackIcon}
                  alt="Back"
                  className="w-[15px] h-[15px] object-contain"
                />
                Back
              </button>

              {/* Home Button */}
              <button
                onClick={() => navigate("/admin")}
                className="flex items-center gap-2 bg-[#C21A17] text-white px-5 py-2 rounded-lg text-sm font-semibold shadow-md hover:bg-[#A91513] transition-all duration-200"
              >
                <img
                  src={HomeIcon}
                  alt="Home"
                  className="w-[15px] h-[15px] object-contain"
                />
                Home
              </button>

            </div>

            {/* Breadcrumb */}
            <div className="mt-6 ml-1 flex items-center flex-wrap text-[14px] text-[#7A7A7A] gap-1">
              <span
                onClick={() => navigate("/admin")}
                className="cursor-pointer"
              >
                {accountName}
              </span>

              <span className="mx-1">
                ›
              </span>

              <span
                onClick={() =>
                  navigate("/dashboard", {
                    state: fullState,
                  })
                }
                className="cursor-pointer"
              >
                {safeRoleName}
              </span>

              <span className="mx-1">
                ›
              </span>

              <span
                onClick={() =>
                  navigate("/experience-configuration", {
                    state: fullState,
                  })
                }
                className="cursor-pointer"
              >
                Experience Configuration
              </span>

              <span className="mx-1">
                ›
              </span>

              <span
                onClick={() =>
                  navigate("/productsConfiguration", {
                    state: fullState,
                  })
                }
                className="cursor-pointer"
              >
                Product Configuration
              </span>

              <span className="mx-1">
                ›
              </span>

              <span
                onClick={() =>
                  navigate("/sync-configuration", {
                    state: fullState,
                  })
                }
                className="cursor-pointer"
              >
                Sync
              </span>

              <span className="mx-1">
                ›
              </span>

              <span className="font-semibold text-[#1B1B1B]">
                Assessment Journey
              </span>
            </div>

          </div>
        </div>

        <h2
          style={{
            fontWeight: 700,
            fontSize: 22,
            margin: "14px 0 2px",
            color: "#222",
          }}
        >
          Assessment Journey
        </h2>

        <p
          style={{
            fontSize: 13,
            color: "#aaa",
            margin: "0 0 20px",
          }}
        >
          Enable and order assessments.
        </p>
      </div>

      <div
        style={{
          padding: "0 40px 40px",
          display: "flex",
          gap: 20,
        }}
      >
        {/* Left panel — toggles */}
        <div
          style={{
            width: 330,
            flexShrink: 0,
            background: "#fff",
            borderRadius: 10,
            padding: "20px 18px 4px",
            boxShadow:
              "0 1px 4px rgba(0,0,0,0.08)",
            alignSelf: "flex-start",
          }}
        >
          <h4
            style={{
              fontWeight: 700,
              fontSize: 18,
              marginBottom: 12,
              color: "#222",
            }}
          >
            Assessments
          </h4>

          <div
            style={{
              borderTop: "1px solid #E8E8E8",
              marginBottom: 18,
            }}
          />

          {ASSESSMENT_DEFINITIONS.map((a) => (
            <div
              key={a.id}
              style={{
                marginBottom: 18,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                border: "1px solid #ECECEC",
                borderRadius: 12,
                padding: "16px 14px",
                background: "#fff",
              }}
            >
              <div
                style={{
                  flex: 1,
                  paddingRight: 10,
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#222",
                  }}
                >
                  {a.name}
                </div>

                <div
                  style={{
                    fontSize: 11,
                    color: "#bbb",
                    marginTop: 3,
                  }}
                >
                  {a.subtitle}
                </div>
              </div>

              <button
                type="button"
                role="switch"
                aria-checked={enabled[a.id]}
                aria-label={`${a.name} ${enabled[a.id] ? "enabled" : "disabled"}`}
                onClick={() =>
                  handleToggleClick(
                    a.id,
                    a.name
                  )
                }
                style={{
                  width: 38,
                  height: 18,
                  border: 0,
                  padding: 0,
                  borderRadius: 9,
                  flexShrink: 0,
                  backgroundColor: enabled[a.id]
                    ? "#DE8584"
                    : "#E2E2E2",
                  position: "relative",
                  cursor: "pointer",
                  transition: "background-color 0.25s",
                  marginTop: 2,
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    backgroundColor: enabled[a.id]
                      ? "#AD0101"
                      : "#bbb",
                    top: -1,
                    left: enabled[a.id]
                      ? 18
                      : 0,
                    transition:
                      "left 0.25s, background-color 0.25s",
                    boxShadow:
                      "0 2px 5px rgba(0,0,0,0.35)",
                  }}
                />
              </button>
            </div>
          ))}
        </div>

        {/* Right panel — drag-and-drop order */}
        <div
          style={{
            flex: 1,
            background: "#fff",
            borderRadius: 10,
            padding: 20,
            boxShadow:
              "0 1px 4px rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <h4
              style={{
                fontWeight: 700,
                fontSize: 14,
                margin: 0,
                color: "#222",
              }}
            >
              Assessment Journey Order
            </h4>

            <span
              style={{
                fontSize: 12,
                color: "#aaa",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <HolderOutlined
                style={{ fontSize: 13 }}
              />
              Drag to reorder
            </span>
          </div>

          {activeOrder.length === 0 && (
            <div
              style={{
                textAlign: "center",
                padding: "40px 20px",
                color: "#ccc",
                fontSize: 14,
              }}
            >
              No assessments enabled. Toggle
              assessments on the left to add them.
            </div>
          )}

          {activeOrder.map((id, index) => {
            const a = getAssessment(id);
            const isDragging =
              draggedId === id;
            const isDragOver =
              dragOverId === id;

            return (
              <div
                key={id}
                draggable
                onDragStart={(e) =>
                  handleDragStart(e, id)
                }
                onDragOver={(e) =>
                  handleDragOver(e, id)
                }
                onDrop={(e) =>
                  handleDrop(e, id)
                }
                onDragEnd={handleDragEnd}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    "space-between",
                  padding: "14px 16px",
                  marginBottom: 12,
                  background: isDragging
                    ? "#fff8f8"
                    : "#fff",
                  border: isDragOver
                    ? "2px dashed #AD0101"
                    : "1px solid #eee",
                  borderRadius: 8,
                  boxShadow: isDragging
                    ? "0 6px 20px rgba(0,0,0,0.12)"
                    : "0 1px 3px rgba(0,0,0,0.06)",
                  opacity: isDragging
                    ? 0.5
                    : 1,
                  cursor: "grab",
                  transition:
                    "border 0.15s, box-shadow 0.15s",
                  userSelect: "none",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                  }}
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: "#AD0101",
                      color: "#fff",
                      fontSize: 13,
                      fontWeight: 700,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {index + 1}
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#222",
                        marginBottom: 5,
                      }}
                    >
                      {a.name}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 18,
                        flexWrap: "wrap",
                      }}
                    >
                      {a.tags.includes(
                        "A Video"
                      ) && (
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 5,
                              color: "#8a8a8a",
                            }}
                          >
                            <UserOutlined
                              style={{
                                fontSize: 14,
                              }}
                            />
                            Video
                          </span>
                        )}

                      {a.tags.includes(
                        "A Quiz"
                      ) && (
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 5,
                              color: "#8a8a8a",
                            }}
                          >
                            <PsychologyAltOutlined
                              sx={{
                                fontSize: 16,
                                transform: "scaleX(-1)",
                              }}
                            />
                            Quiz
                          </span>
                        )}

                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          color: "#8a8a8a",
                        }}
                      >
                        {a.id === "jpFitment" ? (
                          <RiCoinsLine size={16} />
                        ) : (
                          <GlobalOutlined
                            style={{
                              fontSize: 14,
                            }}
                          />
                        )}
                        {a.questions} Questions
                      </span>

                      {a.points && (
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                            color: "#8a8a8a",
                          }}
                        >
                          <RiCoinsLine size={16} />
                          {a.points}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <HolderOutlined
                  style={{
                    fontSize: 16,
                    color: "#ccc",
                    cursor: "grab",
                  }}
                />
              </div>
            );
          })}

          {validationError && (
            <div
              style={{
                marginTop: 8,
                padding: "10px 14px",
                backgroundColor: "#fff2f0",
                border: "1px solid #ffccc7",
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
                color: "#AD0101",
              }}
            >
              <WarningOutlined
                style={{
                  fontSize: 14,
                  color: "#AD0101",
                }}
              />

              {validationError}
            </div>
          )}
        </div>
      </div>

      <Modal
        open={confirmModal.visible}
        onCancel={handleCancelModal}
        footer={null}
        centered
        closable={false}
        width="min(450px, calc(80vw - 32px))"
        styles={{
          body: {
            padding: "8px 8px",
          },
        }}
      >
        <p
          style={{
            fontSize: 18,
            color: "#333",
            lineHeight: 1.4,
            margin: "0 0 34px",
          }}
        >
          Do you want to{" "}
          {confirmModal.willEnable
            ? "activate"
            : "deactivate"}{" "}
          this{" "}
          <strong>
            {confirmModal.name}
          </strong>
          ?
        </p>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 14,
            width: "100%",
          }}
        >
          {confirmModal.willEnable ? (
            <>
              <button
                type="button"
                onClick={handleConfirm}
                style={{
                  minWidth: 145,
                  height: 40,
                  padding: "0 11px",
                  background: "#2e7d32",
                  border: "1px solid #2e7d32",
                  borderRadius: 6,
                  color: "#fff",
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Activate Quiz
              </button>

              <button
                type="button"
                onClick={handleCancelModal}
                style={{
                  minWidth: 110,
                  height: 40,
                  padding: "0 11px",
                  background: "#fff",
                  border: "1px solid #9CA3AF",
                  borderRadius: 6,
                  color: "#6B7280",
                  fontSize: 16,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={handleConfirm}
                style={{
                  minWidth: 145,
                  height: 40,
                  padding: "0 11px",
                  background: "#AD0101",
                  border: "1px solid #AD0101",
                  color: "#fff",
                  fontSize: 16,
                  fontWeight: 600,
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                Deactivate Quiz
              </button>

              <button
                type="button"
                onClick={handleCancelModal}
                style={{
                  minWidth: 110,
                  height: 40,
                  padding: "0 11px",
                  background: "#fff",
                  border: "1px solid #9CA3AF",
                  borderRadius: 6,
                  color: "#6B7280",
                  fontSize: 16,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default AssessmentJourney;